import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { computeScores } from '../lib/scoring';
import { Users, ClipboardList, AlertCircle, Calendar, ArrowRight, Plus, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { fmtDate, daysUntil, scoreColor } from '../lib/format';

const StatCard = ({ icon: Icon, label, value, accent, hint }) => (
  <Card className="border-brand-border">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="text-3xl font-bold text-brand-indigo mt-2">{value}</div>
          {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { user, visibleClients, visibleSessions } = useApp();
  const nav = useNavigate();
  const [q, setQ] = React.useState('');

  const now = new Date();
  const thisMonth = visibleSessions.filter((s) => {
    const d = new Date(s.session_date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const followupsDueToday = visibleSessions.filter((s) => daysUntil(s.follow_up_date) === 0);
  const followupsThisWeek = visibleSessions.filter((s) => { const du = daysUntil(s.follow_up_date); return du !== null && du >= 0 && du <= 7; });

  const recent = [...visibleSessions].sort((a, b) => new Date(b.session_date) - new Date(a.session_date)).slice(0, 6);

  const filteredClients = visibleClients.filter((c) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (c.name?.toLowerCase().includes(s) || c.phone?.toLowerCase().includes(s) || c.city?.toLowerCase().includes(s));
  }).slice(0, 6);

  const getClientName = (id) => visibleClients.find((c) => c.id === id)?.name || 'Unknown';

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Welcome back</div>
          <h1 className="text-3xl font-bold text-brand-indigo">{user?.name || 'Relationship Manager'}</h1>
          <p className="text-sm text-muted-foreground mt-1">EUIN {user?.euin} · Your client book and pending sessions.</p>
        </div>
        <Button onClick={() => nav('/clients')} className="bg-brand-green hover:bg-brand-greenDark text-white shadow-sm">
          <Plus className="h-4 w-4 mr-2" /> Start New MRI
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="My Clients" value={visibleClients.length} accent="bg-brand-indigo" hint="Total on record" />
        <StatCard icon={ClipboardList} label="Sessions This Month" value={thisMonth.length} accent="bg-brand-cyan" hint="Completed / drafts" />
        <StatCard icon={AlertCircle} label="Follow-ups Due Today" value={followupsDueToday.length} accent="bg-brand-red" hint="Requires action" />
        <StatCard icon={Calendar} label="Due This Week" value={followupsThisWeek.length} accent="bg-brand-amber" hint="Within 7 days" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="border-brand-border">
            <CardContent className="p-0">
              <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-brand-indigo">Recent Sessions</div>
                  <div className="text-[11px] text-muted-foreground">Latest financial MRI activity</div>
                </div>
                <Link to="/clients" className="text-xs text-brand-indigo font-semibold hover:underline flex items-center gap-1">View all <ArrowRight className="h-3 w-3" /></Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                      <th className="px-5 py-3 font-semibold">Client</th>
                      <th className="px-5 py-3 font-semibold">Date</th>
                      <th className="px-5 py-3 font-semibold">Overall Score</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((s) => {
                      const sc = computeScores(s);
                      const c = scoreColor(sc.overall);
                      return (
                        <tr key={s.id} className="border-b border-brand-border/50 hover:bg-brand-bg">
                          <td className="px-5 py-3 font-medium text-brand-indigo">{getClientName(s.client_id)}</td>
                          <td className="px-5 py-3 text-muted-foreground">{fmtDate(s.session_date)}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <span className={`h-2 w-2 rounded-full ${c.bg}`} />
                              <span className={`font-semibold ${c.text}`}>{sc.overall}</span>
                              <span className="text-xs text-muted-foreground">/ 100</span>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <Badge variant="outline" className={`capitalize ${s.status === 'completed' ? 'text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-amber-700 border-amber-300 bg-amber-50'}`}>
                              {s.status.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Link to={`/clients/${s.client_id}/mri/${s.id}`} className="text-xs text-brand-indigo font-semibold hover:underline">View</Link>
                          </td>
                        </tr>
                      );
                    })}
                    {recent.length === 0 && (
                      <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-muted-foreground">No sessions yet. Head to Clients to start one.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="border-brand-border">
            <CardContent className="p-5">
              <div className="text-sm font-semibold text-brand-indigo mb-3">Quick Client Search</div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone or city" className="pl-9 h-10" />
              </div>
              <div className="mt-4 space-y-2">
                {filteredClients.map((c) => (
                  <Link key={c.id} to={`/clients/${c.id}`} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-brand-bg transition-colors">
                    <div>
                      <div className="text-sm font-medium text-brand-indigo">{c.name}</div>
                      <div className="text-[11px] text-muted-foreground">{c.city} · {c.phone}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                ))}
                {filteredClients.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">No clients found</div>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
